class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('.predictive-search__results');
    this.overlay = this.querySelector('.predictive-search__overlay');
    this.isOpen = false;
    this.controller = new AbortController();
    this.working = false;
    this.hasPlaceholder = false;
    this.timeoutQuery;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', (event) => {
      this.clearSelectedOption();
      this.open();
      this.setLiveRegionLoadingState();
      if(this.timeoutQuery) {
        clearTimeout(this.timeoutQuery);
        this.working = false;
      }
      this.timeoutQuery = setTimeout(this.onChange.bind(this), 300);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.predictiveSearchResults.addEventListener('mouseover', this.onMouseOver.bind(this));
    this.overlay.addEventListener('click', this.onClickOverlay.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  getOptions() {
    return [...this.predictiveSearchResults.querySelectorAll('[role="option"]')];
  }

  getActiveOption() {
    const activeId = this.input.getAttribute('aria-activedescendant');
    return activeId ? document.getElementById(activeId) : null;
  }

  setSelectedOption(option) {
    this.getOptions().forEach((candidate) => candidate.setAttribute('aria-selected', candidate === option ? 'true' : 'false'));
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  clearSelectedOption() {
    this.getOptions().forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.input.removeAttribute('aria-activedescendant');
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close();
      return;
    }

    const activeOption = this.getActiveOption();
    if (event.key === 'Enter' && activeOption) {
      event.preventDefault();
      activeOption.click();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const options = this.getOptions();
    if (!options.length) return;

    event.preventDefault();
    if (!this.isOpen) this.open();
    const activeIndex = options.indexOf(activeOption);
    const nextIndex = event.key === 'ArrowDown'
      ? (activeIndex + 1) % options.length
      : (activeIndex <= 0 ? options.length : activeIndex) - 1;
    this.setSelectedOption(options[nextIndex]);
  }

  onMouseOver(event) {
    const option = event.target.closest('[role="option"]');
    if (option && this.predictiveSearchResults.contains(option)) this.setSelectedOption(option);
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onClickOverlay() {
    this.close();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    if(this.working) {
      this.controller.abort();
      this.controller = new AbortController();
      this.working = false;
    }
    if (!Shopify.designMode && this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const requestController = this.controller;
    const signal = requestController.signal;
    this.working = true;

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[type]')}=product,collection&section_id=predictive-search`, { signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = getDomHtmlFromText(text, '#shopify-section-predictive-search');
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.removeAttribute('results');
        this.close();
      })
      .finally(() => {
        if (this.controller === requestController) {
          this.working = false;
          this.removeAttribute('loading');
        }
      });
  }

  setLiveRegionLoadingState() {
    this.setAttribute('loading', true);
    if(!this.hasPlaceholder) {
      const temp = document.getElementById("predictive-search-placeholder-tpl");
      const placeholder = temp.content.cloneNode(true);
      this.querySelector('.predictive-search__loading-state').append(placeholder);
      this.hasPlaceholder = true;
    }
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup.innerHTML;

    this.getOptions().forEach((option, index) => {
      option.id = `${this.predictiveSearchResults.id}-Option-${index + 1}`;
      option.setAttribute('aria-selected', 'false');
    });

    this.predictiveSearchResults.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });

    this.setAttribute('results', true);

    this.setLiveRegionResults();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }

  open() {
    if(!this.isOpen) {
      if(!this.classList.contains('run-top')) {
        if(!this.timeoutRunTop) {
          this.timeoutRunTop = setTimeout(() => {
            this.classList.add('run-top');
            this.timeoutRunTop = null;
          }, 500);
        }
      }
      
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    
    this.classList.remove('run-top');

    this.clearSelectedOption();
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
   
    this.isOpen = false;
    if(this.nextElementSibling && this.nextElementSibling.classList.contains('search-modal__close-button')) {
      this.nextElementSibling.dispatchEvent(new Event('click'));
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
